import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, Tab, TextField } from '@mui/material'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../../context/ContextProvider'
import axios from '../../../lib/axios'
import Loading from '../../../components/Loading'
import { handleAxiosError, handleInteger, handleNumber, handleZero, deleteZero, isValid } from '../../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../../constants/constants'

const UpdateQuotePricingTier = () => {

    const { t } = useTranslation()
    const { company_id, config, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [isLoading, setIsLoading] = useState(false)
    const [customers, setCustomers] = useState([])
    const [items, setItems] = useState([])

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [customerName, setCustomerName] = useState('')
    const [itemId, setItemId] = useState('')
    const [itemLabel, setItemLabel] = useState('')
    const [minQuantity, setMinQuantity] = useState('')
    const [maxQuantity, setMaxQuantity] = useState('')
    const [unitPrice, setUnitPrice] = useState('')
    const [effectiveFrom, setEffectiveFrom] = useState(null)
    const [effectiveTo, setEffectiveTo] = useState(null)

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/quote-pricing-tiers')
    }

    const isFormValid = () => (
        isValid(customerId)
        && isValid(itemId)
        && minQuantity !== ''
        && unitPrice !== ''
    )

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                goBack()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [location])

    useEffect(() => {
        getPricingTier()
    }, [params.id])

    /* real-time: refresh if another user updates this pricing tier, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-quotepricingtier-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-quotepricingtier-deleted`)

        channelUpdated.bind(`${clientId}-quotepricingtier-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getPricingTier()
            }
        })

        channelDeleted.bind(`${clientId}-quotepricingtier-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-quotepricingtier-updated`)
            pusher.unsubscribe(`${clientId}-quotepricingtier-deleted`)
        })
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getCustomers(company_id)
            getItems(company_id)
        }
    }, [company_id])

    /* requests */

    const getPricingTier = async () => {
        setIsLoading(true)
        await axios.get(`/api/quote-pricing-tiers/${params.id}`, config)
            .then(res => {
                const tier = res.data?.data

                setCustomerId(tier?.customer_id ?? '')
                setCustomerName(tier?.customer_name ?? '')
                setItemId(tier?.item_id ?? '')
                setItemLabel(tier?.item_no ?? '')
                setMinQuantity(isValid(tier?.min_quantity) ? String(tier.min_quantity) : '0')
                setMaxQuantity(isValid(tier?.max_quantity) ? String(tier.max_quantity) : '')
                setUnitPrice(isValid(tier?.unit_price) ? String(tier.unit_price) : '')
                setEffectiveFrom(tier?.effective_from ? dayjs(tier.effective_from) : null)
                setEffectiveTo(tier?.effective_to ? dayjs(tier.effective_to) : null)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getCustomers = async (id) => {
        await axios
            .get(`/api/customers?company_id=${id}&blocked=0`, config)
            .then(res => {
                setCustomers(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getItems = async (id) => {
        await axios
            .get(`/api/items?blocked_for_sales=0&blocked=0&company_id=${id}`, config)
            .then(res => {
                setItems(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const updatePricingTier = async (e) => {
        e.preventDefault()
        setIsLoading(true)

        const formData = {}

        formData['company_id'] = Number(company_id)
        formData['customer_id'] = Number(customerId)
        formData['item_id'] = Number(itemId)
        formData['min_quantity'] = Number(minQuantity)
        formData['max_quantity'] = maxQuantity !== '' ? Number(maxQuantity) : null
        formData['unit_price'] = Number(unitPrice)
        formData['effective_from'] = effectiveFrom ? dayjs(effectiveFrom).format('YYYY-MM-DD') : null
        formData['effective_to'] = effectiveTo ? dayjs(effectiveTo).format('YYYY-MM-DD') : null

        await axios.put(`/api/quote-pricing-tiers/${params.id}`, formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })

        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    /* handlers */

    const handleCustomerChange = (a, b) => {
        setCustomerId(b?.id || '')
        setCustomerName(b?.label || '')
    }

    const handleItemChange = (a, b) => {
        setItemId(b?.id || '')
        setItemLabel(b?.label || '')
    }

    const handleEffectiveFromChange = (newValue) => {
        setEffectiveFrom(newValue)
        if (newValue && effectiveTo && dayjs(effectiveTo).isBefore(dayjs(newValue), 'day')) {
            setEffectiveTo(newValue)
        }
    }

    const optionsCustomers = customers?.map(option => ({ label: option.customer_no + ' - ' + option.name, id: option.id }))
    const optionsItems = items?.map(option => ({ label: option.item_no + ' - ' + option.description, id: option.id }))

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='w-full min-h-screen bg-white'>
                    <div className='flex border-b justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-center justify-between w-full leading-none'>
                            <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('edit_pricing_tier')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                                <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <TabContext value="1">
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList aria-label="pricing tier tabs">
                                <Tab sx={{ textTransform: 'none' }} value="1" label={t('general')} icon={<i className="fa-solid fa-circle-info"></i>} iconPosition='start' />
                            </TabList>
                        </Box>
                        <TabPanel value="1">
                            <form className='flex justify-center flex-col items-start mt-2'>
                                {/* Customer */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('customer')} *</p>
                                    <Autocomplete
                                        disablePortal
                                        id="customers-autocomplete"
                                        options={optionsCustomers}
                                        value={customerId ? { label: customerName, id: customerId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        onChange={handleCustomerChange}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>

                                {/* Item */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('item')} *</p>
                                    <Autocomplete
                                        disablePortal
                                        id="items-autocomplete"
                                        options={optionsItems}
                                        value={itemId ? { label: itemLabel, id: itemId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        onChange={handleItemChange}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>

                                {/* Minimum Quantity */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('minimum_quantity')} *</p>
                                    <TextField
                                        sx={TEXTFIELD_STYLE}
                                        type="text"
                                        name="min_quantity"
                                        value={minQuantity}
                                        onChange={(e) => handleInteger({ event: e, setter: setMinQuantity })}
                                        onBlur={e => handleZero({ event: e, setter: setMinQuantity })}
                                        onFocus={e => deleteZero({ event: e, setter: setMinQuantity, value: minQuantity })}
                                    />
                                </div>

                                {/* Maximum Quantity */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('maximum_quantity')}</p>
                                    <TextField
                                        sx={TEXTFIELD_STYLE}
                                        type="text"
                                        name="max_quantity"
                                        value={maxQuantity}
                                        onChange={(e) => handleInteger({ event: e, setter: setMaxQuantity })}
                                    />
                                </div>

                                {/* Unit Price */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('unit_price')} *</p>
                                    <TextField
                                        sx={TEXTFIELD_STYLE}
                                        type="text"
                                        name="unit_price"
                                        value={unitPrice}
                                        onChange={(e) => handleNumber({ event: e, setter: setUnitPrice, precision: 2 })}
                                        onBlur={e => handleZero({ event: e, setter: setUnitPrice })}
                                        onFocus={e => deleteZero({ event: e, setter: setUnitPrice, value: unitPrice })}
                                    />
                                </div>

                                {/* Effective From */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('effective_from')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={TEXTFIELD_STYLE}
                                            format="YYYY-MM-DD"
                                            value={effectiveFrom}
                                            onChange={handleEffectiveFromChange}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Effective To */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('effective_to')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={TEXTFIELD_STYLE}
                                            format="YYYY-MM-DD"
                                            value={effectiveTo}
                                            minDate={effectiveFrom || undefined}
                                            onChange={(newValue) => setEffectiveTo(newValue)}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </form>
                        </TabPanel>
                    </TabContext>

                    <div className='w-full flex justify-end px-6 pb-8'>
                        <button type="button" disabled={!isFormValid()} onClick={updatePricingTier} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#015D9F] hover:bg-[#003459] disabled:bg-[#B8B7BC]'>
                            <span className='pr-2'><i className="fa-solid fa-clock-rotate-left"></i></span> {t('update')}
                        </button>
                    </div>
                </div>
            </Box>
        </>
    )
}

export default UpdateQuotePricingTier
